import _extends from "@babel/runtime/helpers/extends";
import cx from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { tombac } from "../../shared";
import { propStyling } from "../../shared/propStyling";
import { extractStylingProps } from "../../shared/propStyling/propStylingUtils";
const Container = styled.span.withConfig({
  displayName: "Checkbox__Container"
})(["all:initial;cursor:", ";display:inline-block;", " ", ""], _ref => {
  let {
    disabled
  } = _ref;
  return disabled ? "default" : "pointer";
}, _ref2 => {
  let {
    disabled
  } = _ref2;
  return tombac.text({
    color: disabled ? tombac.alpha("neutral", 24) : undefined,
    fontSize: 14
  });
}, propStyling);
const Input = styled.input.withConfig({
  displayName: "Checkbox__Input"
})(["all:unset;height:0;margin:0;opacity:0;width:0;"]);
const Label = styled.span.withConfig({
  displayName: "Checkbox__Label"
})(["all:unset;font-size:", ";margin-left:", ";margin-right:", ";"], _ref3 => {
  let {
    size
  } = _ref3;
  return size === "m" ? tombac.unit(16) : undefined;
}, _ref4 => {
  let {
    placement
  } = _ref4;
  return placement === "after" ? tombac.space(1) : undefined;
}, _ref5 => {
  let {
    placement
  } = _ref5;
  return placement === "before" ? tombac.space(1) : undefined;
});
const StyledCheckbox = styled.span.withConfig({
  displayName: "Checkbox__StyledCheckbox"
})(["all:unset;border-radius:50%;display:inline-block;height:", ";width:", ";transition:0.1s ease-out;transition-property:box-shadow,margin,padding;vertical-align:", ";&::before{background-color:", ";border:", " solid ", ";border-radius:3px;box-sizing:border-box;content:\"\";display:block;height:100%;transition:0.1s ease-out;transition-property:background-color,border-color;width:100%;}&::after{border:0 solid transparent;box-sizing:border-box;content:\"\";display:block;height:0;margin:-50% 0 0 50%;transform:translate(-50%,-50%) rotate(90deg);transition:0.1s ease-out;transition-property:border-color,border-width,height,transform,transform-origin,width;width:0;}", ":disabled + &::before{background-color:", ";}", ":focus + &,", ":focus-visible + &{background-color:", ";box-shadow:", " hsla(206,70%,74%,0.24);margin:", ";padding:", ";border-radius:", ";}", ":focus:not(:focus-visible) + &{background-color:transparent;box-shadow:none;margin:0;padding:0;}", ":focus + &::before,", ":focus-visible + &::before{background-color:", ";border-color:", ";}", ":focus:not(:focus-visible) + &::before{background-color:", ";border-color:", ";}", ":not(:disabled) + &:hover::before{background-color:", ";border-color:", ";}", ":focus + &:hover::before,", ":focus-visible + &:hover::before{background-color:", ";border-color:", ";}", ":focus:not(:focus-visible) + &:hover::before{background-color:", ";border-color:", ";}", ":not(:disabled):checked + &::before,", ":not(:disabled):indeterminate + &::before{background-color:", ";border-color:", ";}", ":not(:disabled):checked + &:hover::before,", ":not(:disabled):indeterminate + &:hover::before{background-color:", ";border-color:", ";}", ":checked + &::after,", ":indeterminate + &::after{border-color:", ";}", ":disabled:checked + &::after,", ":disabled:indeterminate + &::after{border-color:", ";}", ":checked + &::after{border-width:", ";transform:translate(-50%,-50%) rotate(45deg);transform-origin:75% 50%;}", ":indeterminate + &::after{border-width:", ";transform:translate(-50%,-50%) rotate(90deg);transform-origin:50% 50%;width:0;}", ""], _ref6 => {
  let {
    size
  } = _ref6;
  return tombac.unit(size === "m" ? 24 : size === "s" ? 18 : 16);
}, _ref7 => {
  let {
    size
  } = _ref7;
  return tombac.unit(size === "m" ? 24 : size === "s" ? 18 : 16);
}, _ref8 => {
  let {
    size
  } = _ref8;
  return tombac.unit(size === "m" ? -7 : size === "s" ? -4 : -3);
}, tombac.color.white, tombac.unit(1), tombac.color("neutral", 400), Input, tombac.color("neutral", 500), Input, Input, tombac.color("primary", 300), tombac.unit(0, 1, 3, 0), tombac.unit(-3), tombac.unit(3), tombac.unit(4), Input, Input, Input, tombac.color("primary", 200), tombac.color("primary", 500), Input, tombac.color.white, tombac.color("neutral", 500), Input, tombac.color("neutral", 300), tombac.color("neutral", 500), Input, Input, tombac.color("primary", 200), tombac.color("primary", 600), Input, tombac.color("neutral", 300), tombac.color("neutral", 500), Input, Input, tombac.color("primary", 500), tombac.color("primary", 500), Input, Input, tombac.color("primary", 600), tombac.color("primary", 600), Input, Input, tombac.color.white, Input, Input, tombac.color.white, Input, tombac.unit(0, 2, 2, 0), Input, tombac.unit(0, 2, 0, 0), _ref9 => {
  let {
    size
  } = _ref9;
  return css(["", ":checked + &.TombacStyledCheckbox__size-", "::after,", ":indeterminate + &.TombacStyledCheckbox__size-", "::after{height:", ";}", ":checked + &.TombacStyledCheckbox__size-", "::after{width:", ";}"], Input, size, Input, size, tombac.unit(size === "m" ? 10 : 8), Input, size, tombac.unit(size === "m" ? 6 : 5));
});
const StyledToggle = styled.span.withConfig({
  displayName: "Checkbox__StyledToggle"
})(["all:unset;background-color:", ";border:", " solid transparent;border-radius:", ";box-sizing:border-box;display:inline-block;height:", ";position:relative;transition:0.1s ease-out;transition-property:background-color,border;vertical-align:", ";width:", ";&::before{border:0 solid transparent;border-radius:", ";bottom:0;content:\"\";left:0;margin:0;position:absolute;right:0;top:0;transition:0.1s ease-out;transition-property:border,box-shadow,margin;z-index:0;}&::after{background-color:", ";box-shadow:", " ", ";content:\"\";display:block;border-radius:100%;height:", ";left:", ";position:absolute;width:", ";top:", ";transition:left 0.1s ease-out;}&:hover{background-color:", ";}", ":focus + &,", ":focus-visible + &{border-color:", ";}", ":focus:not(:focus-visible) + &{border-color:transparent;}", ":focus + &::before,", ":focus-visible + &::before{border:", " solid ", ";box-shadow:", " hsla(206,70%,74%,0.24);margin:", ";}", ":focus:not(:focus-visible) + &::before{border:0 solid transparent;box-shadow:none;margin:0;}", ":checked + &{background-color:", ";}", ":checked + &:hover{background-color:", ";}", ":checked + &&::after{left:", ";}", ":disabled + &{background-color:", " !important;}", ":disabled + &::after{background-color:", ";box-shadow:none;}"], tombac.color("neutral", 500), tombac.unit(1), tombac.unit(12), _ref10 => {
  let {
    size
  } = _ref10;
  return tombac.unit(size === "xs" ? 16 : 24);
}, _ref11 => {
  let {
    size
  } = _ref11;
  return tombac.unit(size === "xs" ? -3 : -7);
}, _ref12 => {
  let {
    size
  } = _ref12;
  return tombac.unit(size === "xs" ? 32 : 46);
}, tombac.unit(16), tombac.color.white, tombac.unit(0, 1, 4, 0), tombac.alpha("neutral", 24), _ref13 => {
  let {
    size
  } = _ref13;
  return tombac.unit(size === "xs" ? 14 : 20);
}, _ref14 => {
  let {
    size
  } = _ref14;
  return tombac.unit(size === "xs" ? 0 : 1);
}, _ref15 => {
  let {
    size
  } = _ref15;
  return tombac.unit(size === "xs" ? 14 : 20);
}, _ref16 => {
  let {
    size
  } = _ref16;
  return tombac.unit(size === "xs" ? 0 : 1);
}, tombac.color("neutral", 600), Input, Input, tombac.color("primary", 500), Input, Input, Input, tombac.unit(3), tombac.color("primary", 300), tombac.unit(0, 1, 3, 0), tombac.unit(-4), Input, Input, tombac.color("primary", 500), Input, tombac.color("primary", 600), Input, _ref17 => {
  let {
    size
  } = _ref17;
  return tombac.unit(size === "xs" ? 16 : 23);
}, Input, tombac.color("neutral", 400), Input, tombac.color("neutral", 500));
export const Checkbox = _ref18 => {
  let {
    className,
    checked,
    disabled,
    indeterminate = false,
    label = true,
    labelPlacement = "after",
    size = "s",
    style,
    variant = "checkbox",
    ...rest
  } = _ref18;
  const inputRef = useRef(null);
  const [stylingProps, nonStylingProps] = extractStylingProps(rest);
  const handleMouseDown = useCallback(e => e.preventDefault(), []);
  useEffect(function handleIndeterminate() {
    const checkbox = inputRef.current;
    const setIndeterminate = () => {
      checkbox.indeterminate = indeterminate;
    };
    setIndeterminate();
    checkbox.addEventListener("change", setIndeterminate);
    return () => {
      checkbox.removeEventListener("change", setIndeterminate);
    };
  }, [checked, indeterminate]);
  const displayLabel = typeof label !== "boolean";
  return /*#__PURE__*/React.createElement(Container, _extends({
    as: label === false ? undefined : "label",
    className: cx("TombacCheckbox", className),
    disabled: disabled,
    onMouseDown: handleMouseDown,
    style: style
  }, stylingProps), displayLabel && labelPlacement === "before" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
    placement: "before",
    size: size
  }, label), " "), /*#__PURE__*/React.createElement(Input, _extends({
    checked: indeterminate ? false : checked,
    disabled: disabled,
    ref: inputRef,
    type: "checkbox"
  }, nonStylingProps)), variant === "checkbox" && /*#__PURE__*/React.createElement(StyledCheckbox, {
    className: `TombacStyledCheckbox__size-${size}`,
    size: size
  }), variant === "toggle" && /*#__PURE__*/React.createElement(StyledToggle, {
    size: size
  }), displayLabel && labelPlacement === "after" && /*#__PURE__*/React.createElement(React.Fragment, null, " ", /*#__PURE__*/React.createElement(Label, {
    placement: "after",
    size: size
  }, label)));
};
export const Toggle = props => /*#__PURE__*/React.createElement(Checkbox, _extends({}, props, {
  variant: "toggle"
}));
//# sourceMappingURL=Checkbox.js.map